import cx from 'classnames';
import { ChevronDown } from 'ethena-icons';
import { AnimatePresence, motion } from 'framer-motion';
import _ from 'lodash';
import {
  createContext,
  ElementType,
  JSX,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Button, ButtonProps } from '../Button';
import css from './Accordion.module.scss';

const AccordionContext = createContext({
  isExpanded: false,
  toggle: _.noop,
});

const useAccordionContext = () => useContext(AccordionContext);

interface IAccordionProps {
  children: ReactNode;
  isDefaultExpanded?: boolean;
}

const Accordion = ({
  children,
  isDefaultExpanded = false,
}: IAccordionProps) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(isDefaultExpanded);

  const toggle = () => setIsExpanded(!isExpanded);

  useEffect(() => {
    setIsExpanded(isDefaultExpanded);
  }, [isDefaultExpanded]);

  return (
    <AccordionContext.Provider
      value={{
        isExpanded,
        toggle,
      }}
    >
      {children}
    </AccordionContext.Provider>
  );
};

interface ICollapseProps {
  children: ReactNode;
}

const Collapse = ({ children }: ICollapseProps) => {
  const { isExpanded } = useAccordionContext();

  const initial = {
    height: 0,
    opacity: 0,
  };

  const animate = {
    height: 'auto',
    opacity: 1,
    transition: {
      height: { duration: 0.3 },
      opacity: { duration: 0.2, delay: 0.15 },
    },
  };

  const exit = {
    height: 0,
    opacity: 0,
    transition: {
      height: { duration: 0.3 },
      opacity: { duration: 0.12 },
    },
  };

  return (
    <AnimatePresence mode="wait">
      {isExpanded && (
        <motion.div initial={initial} animate={animate} exit={exit}>
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export type AccordionToggleProps = Omit<ButtonProps, 'children'> & {
  as?: string | ElementType;
  children:
    | ReactNode
    | ((props: { isExpanded: boolean; toggle: () => void }) => JSX.Element);
  className?: string;
};

const Toggle = ({ children, className, ...rest }: AccordionToggleProps) => {
  const { isExpanded, toggle } = useAccordionContext();

  return _.isFunction(children) ? (
    children({ isExpanded, toggle })
  ) : (
    <Button
      aria-expanded={!isExpanded}
      className={cx(css.toggle, className, { [css.expanded]: isExpanded })}
      onClick={toggle}
      variant="link"
      {...rest}
    >
      {children}
      <motion.span
        className={css.arrow}
        animate={{
          rotate: isExpanded ? 180 : 0,
        }}
        transition={{
          duration: 0.15,
          ease: 'easeInOut',
        }}
      >
        <ChevronDown />
      </motion.span>
    </Button>
  );
};

Accordion.Collapse = Collapse;
Accordion.Toggle = Toggle;

export { Accordion };
